import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FAQItemProps {
  question: string;
  answer: string | React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
  itemClassName?: string;
  questionClassName?: string;
  answerClassName?: string;
}

export interface FAQItemType {
  question: string;
  answer: string | React.ReactNode;
  id?: string;
}

interface FAQProps {
  faqs: FAQItemType[];
  initialOpenIndex?: number | null;
  className?: string;
  itemClassName?: string;
  questionClassName?: string;
  answerClassName?: string;
  title?: string;
  subtitle?: string;
}

export function FAQItem({ 
  question, 
  answer, 
  isOpen, 
  onClick,
  itemClassName = '',
  questionClassName = '',
  answerClassName = ''
}: FAQItemProps) {
  return (
    <div className={`border-b border-gray-200 dark:border-gray-700 py-4 ${itemClassName}`}>
      <button
        className={`flex justify-between items-center w-full text-left focus:outline-none group ${questionClassName}`}
        onClick={onClick}
        aria-expanded={isOpen}
      >
        <h3 className={`text-lg font-medium text-gray-900 dark:text-white group-hover:text-primary transition-colors ${questionClassName}`}>
          {question}
        </h3>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div 
          className={`overflow-hidden transition-all duration-300 ease-in-out ${answerClassName}`}
          style={{
            maxHeight: isOpen ? '200px' : '0px',
            opacity: isOpen ? 1 : 0,
            marginTop: isOpen ? '0.5rem' : '0',
          }}
        >
          <div className={`text-gray-600 dark:text-gray-300 pb-2 ${answerClassName}`}>
            {typeof answer === 'string' ? <p>{answer}</p> : answer}
          </div>
        </div>
      )}
    </div>
  );
}

export function FAQ({
  faqs,
  initialOpenIndex = 0,
  className = '',
  itemClassName = '',
  questionClassName = '',
  answerClassName = '',
  title,
  subtitle
}: FAQProps) {

  const [openIndex, setOpenIndex] = useState<number | null>(initialOpenIndex);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className={`${className}`}>
      {(title || subtitle) && (
        <div className="text-center mb-12">
          {title && (
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-300">
              {subtitle}
            </p>
          )}
        </div>
      )}

      <div className="max-w-3xl mx-auto divide-y divide-gray-200 dark:divide-gray-700">
        {faqs.map((faq, index) => (
          <FAQItem
            key={faq.id || index}
            question={faq.question}
            answer={faq.answer}
            isOpen={openIndex === index}
            onClick={() => toggleFAQ(index)}
            itemClassName={itemClassName}
            questionClassName={questionClassName}
            answerClassName={answerClassName}
          />
        ))}
      </div>
    </div>
  );
}

export default function FAQSection() {
  const { t } = useTranslation();
  
  const faqs = [
    {
      id: 'q1',
      question: t('faq.q1.question'),
      answer: t('faq.q1.answer')
    },
    {
      id: 'q2',
      question: t('faq.q2.question'),
      answer: t('faq.q2.answer')
    },
    {
      id: 'q3',
      question: t('faq.q3.question'),
      answer: t('faq.q3.answer')
    },
    {
      id: 'q4',
      question: t('faq.q4.question'),
      answer: t('faq.q4.answer')
    }
  ];

  return (
    <section id="faq" className="bg-white dark:bg-slate-800 py-16 scroll-mt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <FAQ 
          faqs={faqs}
          title={t('faq.title', 'Frequently Asked Questions')}
          subtitle={t('faq.subtitle', 'Everything you need to know about Oley Security')}
        />
      </div>
    </section>
  );
}
